<template>
  <modal
    name="DownloadModal"
    :width="450"
    :height="'auto'"
    :adaptive="true"
    classes="v--modal download-modal-shell"
    class="premium-modal"
    @before-open="prefillFilename"
  >
    <div class="modal-content">
      <div class="modal-header">
        <h2>Exportar Datos de Capa</h2>
        <button class="close-button" @click="$modal.hide('DownloadModal')">&times;</button>
      </div>

      <div class="modal-body">
        <p class="description">Seleccione el formato y el alcance de los datos que desea descargar.</p>

        <!-- Selección de Formato -->
        <div class="option-section">
          <label>Formato de Archivo</label>
          <div class="button-group">
            <button
              :class="{ active: formats.includes('csv') }"
              @click="toggleFormat('csv')"
            >
              CSV (Excel)
            </button>
            <button
              :class="{ active: formats.includes('geojson') }"
              @click="toggleFormat('geojson')"
            >
              GeoJSON
            </button>
            <button
              :class="{ active: formats.includes('shp') }"
              @click="toggleFormat('shp')"
            >
              SHP
            </button>
            <button
              :class="{ active: formats.includes('kmz') }"
              @click="toggleFormat('kmz')"
            >
              KMZ
            </button>
          </div>
        </div>

        <!-- Selección de Alcance -->
        <div class="option-section">
          <label>Alcance de los Datos</label>
          <div class="selection-cards">
            <div 
              class="selection-card" 
              :class="{ active: useFilters }" 
              @click="useFilters = true"
            >
              <div class="card-title">Datos Filtrados</div>
              <div class="card-desc">Solo lo que se muestra actualmente con filtros aplicados.</div>
            </div>
            <div 
              class="selection-card" 
              :class="{ active: !useFilters }" 
              @click="useFilters = false"
            >
              <div class="card-title">Todos los Datos</div>
              <div class="card-desc">La capa completa sin filtros (límite de 10.000 registros).</div>
            </div>
          </div>
        </div>

        <!-- Nombre del archivo (#76) -->
        <div class="option-section">
          <label for="download-filename">Nombre del archivo</label>
          <input
            id="download-filename"
            v-model="filename"
            type="text"
            class="filename-input"
            placeholder="Nombre del archivo descargado"
          />
        </div>

        <!-- Campo para el nombre del marcador en KMZ -->
        <div class="option-section" v-if="formats.includes('kmz')">
          <SearchSelect
            v-model="kmlNameField"
            label="Columna para el nombre del marcador (KMZ)"
            placeholder="Sin título"
            empty-message="La capa no tiene columnas disponibles"
            :options="kmlNameFieldOptions"
          />
        </div>

        <div class="limit-info">
          <font-awesome-icon icon="info-circle" />
          <span>Máximo de 10.000 registros por descarga.</span>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="$modal.hide('DownloadModal')">Cancelar</button>
        <button
          class="btn btn-primary"
          @click="executeDownload"
          :disabled="loading || formats.length === 0"
        >
          <font-awesome-icon v-if="loading" icon="spinner" spin />
          {{ loading ? 'Preparando...' : 'Descargar' }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { attributesTableService } from "@/services/layers/attributesTable";
import SearchSelect from "@/components/common/pickers/SearchSelect.vue";

export default {
  name: "DownloadModal",
  components: { SearchSelect },
  data() {
    return {
      formats: [],
      useFilters: true,
      loading: false,
      filename: "",
      kmlNameField: null
    };
  },
  computed: {
    layer() {
      return this.$store.getters["attributeTable/layer"];
    },
    appliedFilter() {
      return this.$store.getters["attributeTable/appliedFilter"];
    },
    fieldSchema() {
      return this.$store.getters["attributeTable/fieldSchema"] || {};
    },
    fieldAliases() {
      return this.$store.getters["attributeTable/fieldAliases"] || {};
    },
    kmlNameFieldOptions() {
      return Object.keys(this.fieldSchema)
        .filter(field => !["bbox", "geometry"].includes(field) && this.fieldSchema[field] !== "geometry")
        .map(field => ({ id: field, label: this.fieldAliases[field] || field }));
    }
  },
  methods: {
    // #76: precarga el nombre predeterminado actual; vacío mantiene el predeterminado
    prefillFilename() {
      this.filename = this.layer ? `${this.layer.name.replace(/\s+/g, "_")}_${Date.now()}` : "";
      // Capas importadas desde KML/KMZ ya traen una columna "name" (así la
      // lee ol/format/KML al importar) — preseleccionarla evita que el
      // usuario tenga que volver a elegir lo que la capa ya tenía.
      const nameField = this.kmlNameFieldOptions.find(option => option.id.toLowerCase() === "name");
      this.kmlNameField = nameField ? nameField.id : null;
    },
    toggleFormat(fmt) {
      const idx = this.formats.indexOf(fmt);
      if (idx === -1) {
        this.formats.push(fmt);
      } else {
        this.formats.splice(idx, 1);
      }
    },
    async executeDownload() {
      if (!this.layer || this.loading || this.formats.length === 0) return;
      this.loading = true;

      try {
        await attributesTableService.downloadFeatures(
          this.layer,
          this.useFilters ? this.appliedFilter : { version: 2, mode: "none" },
          this.formats,
          this.filename,
          this.kmlNameField
        );
        this.$modal.hide("DownloadModal");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.premium-modal::v-deep .download-modal-shell {
  overflow: hidden;
  border: 1px solid rgba(59, 110, 148, 0.3);
  border-radius: 12px;
  background: var(--color-white);
  box-shadow: 0 20px 54px rgba(8, 28, 44, 0.28);
}

.modal-content {
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: var(--color-white);
  box-shadow: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: var(--color-graphite);
  font-family: 'Inter', sans-serif;
}

.modal-header {
  padding: 18px 24px;
  border-bottom: 3px solid var(--color-orange);
  background: var(--color-navy);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-white);
}

.close-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 1.1rem;
  cursor: pointer;
  color: var(--color-fog);
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.close-button:hover {
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.18);
  color: var(--color-white);
}

.modal-body {
  padding: 24px;
}

.description {
  margin-top: 0;
  margin-bottom: 20px;
  color: #7f8c8d;
  font-size: 0.95rem;
}

.option-section {
  margin-bottom: 24px;
}

.option-section label {
  display: block;
  font-weight: 600;
  margin-bottom: 10px;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #34495e;
}

.button-group {
  display: flex;
  gap: 10px;
}

.button-group button {
  flex: 1;
  padding: 10px;
  border: 1px solid var(--color-line);
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.button-group button.active {
  background: var(--color-orange);
  border-color: var(--color-orange);
  color: var(--color-white);
}

.filename-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  font-size: 0.9rem;
}

.selection-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.selection-card {
  padding: 14px;
  border: 2px solid var(--color-line);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.selection-card:hover {
  border-color: var(--color-orange);
  background: var(--color-orange-light);
}

.selection-card.active {
  border-color: var(--color-orange);
  background: var(--color-orange-light);
}

.card-title {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 4px;
}

.card-desc {
  font-size: 0.85rem;
  color: #7f8c8d;
}

.limit-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fff9e6;
  border-radius: 8px;
  color: #856404;
  font-size: 0.85rem;
}

.modal-footer {
  padding: 18px 24px;
  border-top: 1px solid var(--color-line);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  background: var(--color-mist);
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s;
  border: none;
}

.btn-secondary {
  background: #e0e0e0;
  color: #333;
}

.btn-primary {
  background: var(--color-orange);
  color: var(--color-white);
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-orange);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
